import React from "react";
import { useFocusable } from "@noriginmedia/norigin-spatial-navigation";
import { getFormattedTime } from "./utils/shared/time";
import { getEventCardStatus } from "./utils/shared/eventStatus";
import { getThumbnailUrl } from "./utils/shared/images";
import { EVENT_STATUS, CONTENT_TYPE } from "./constants/event";
import { getModelConfig } from "../utils/models";
import nowPlayingSvg from "../assets/nowPlaying.svg";
import hasPregame from "../assets/hasPregame.svg";
import checkScheduleSvg from "../assets/checkSchedule.svg";
import "./epg.scss";

const renderParsedTime = (event) => {
    const mainStartTime = (event.information && event.information.main_start_time) || event.schedule_start;

    return (
        <p className="eventCardTime">
            <span>Pregame&nbsp;{getFormattedTime(event.schedule_start)}</span>
            <span className="eventCardTimeDivider" />
            <span>Event Start&nbsp;{getFormattedTime(mainStartTime)}</span>
        </p>
    );
};

const renderEventStatus = (event, eventStatus) => {
    const { isNowPlaying, isScheduledFlag, liveButtonText } = eventStatus;

    return (
        <div className={"liveContainer"}>
            <div
                className={`
          ${"liveButton"}
          ${event.status === EVENT_STATUS.LIVE || event.status === EVENT_STATUS.COMING ? "live" : ""}
          ${isNowPlaying ? "nowPlaying" : ""}
          ${isScheduledFlag ? "scheduled" : ""}
        `}
            >
                {isNowPlaying && <img src={nowPlayingSvg} alt="Now playing" />}
                {isNowPlaying ? EVENT_STATUS.NOW_PLAYING : liveButtonText}
            </div>
            {event.schedule_start && (
                <div className="hasPregame">
                    <img src={hasPregame} alt="Has pregame" />
                </div>
            )}
        </div>
    );
};

const EventCardEPG = React.memo(({ event, onFocus, onEnterPress, index, rowId, activeLiveContentId, activeLiveContentType }) => {
    const useBackgroundImage = !!getModelConfig().epg?.useBackgroundImage;
    const { thumbnail: img, title, id } = event;
    const focusKey = `event-${rowId}-${event.id}`;

    const { ref, focused } = useFocusable({
        focusKey,
        onEnterPress,
        onFocus: (focusProps) => {
            onFocus({
                ...focusProps,
                index,
            });
        },
        extraProps: {
            event,
            focusKey,
        },
    });

    const isChannel = event.contentType === CONTENT_TYPE.LIVE_CHANNEL;

    const eventStatus = React.useMemo(() => {
        const activeLiveContent = activeLiveContentId ? { id: activeLiveContentId, contentType: activeLiveContentType } : null;
        const status = getEventCardStatus(event, activeLiveContent);
        if (!status.isLive && !status.isNowPlaying) {
            return { ...status, liveButtonText: <span>{status.liveButtonText}</span> };
        }
        return status;
    }, [event.schedule_start, event.schedule_end, event.status, event.contentType, activeLiveContentId, activeLiveContentType]);

    return (
        <div ref={ref} className={`eventCard ${focused ? "focusedCard" : ""}`} onClick={() => onEnterPress({ event, focusKey })} style={{ cursor: 'pointer' }}>
            <div className={`imageContainer ${!useBackgroundImage && focused ? "focusedItem" : ""}`}>
                {useBackgroundImage ? (
                    <div
                        className={`eventCardImg ${focused ? "focusedItem" : ""}`}
                        style={{ backgroundImage: `url(${getThumbnailUrl(event.thumbnail)})` }}
                        role="img"
                        aria-label={event.title}
                    />
                ) : (
                    <img
                        src={getThumbnailUrl(event.thumbnail)}
                        loading="lazy"
                        decoding="async"
                        className="eventCardImg"
                        alt={event.title}
                    />
                )}
                {eventStatus.isScheduledFlag && event.isDeviceSubscribed && (
                    <div className="subscribedCheck">
                        <img src={checkScheduleSvg} alt="Subscribed" width="36" height="36" />
                    </div>
                )}
                {renderEventStatus(event, eventStatus)}
            </div>
            {!isChannel && renderParsedTime(event)}
            <p className="eventCardTitle">{title}</p>
            {!isChannel && <p className="eventCardLabel">{event.label}</p>}
        </div>
    );
});

export default EventCardEPG;
